import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PostRequest } from "@/lib/http";
import { toast } from "sonner";

const NewPasswordForm = () => {
  const [currentInputType, setCurrentInputType] = useState("password");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();

  const toggleCurrentPasswordVisibility = () => {
    setCurrentInputType((prevType) =>
      prevType === "password" ? "text" : "password",
    );
  };

  const onSubmit = async (body) => {
    const { ...rest } = body;

    const { err } = await PostRequest({
      url: "/api/password-reset/complete",
      body: {
        ...rest,
        email: localStorage.getItem("userEmail"),
        code: localStorage.getItem("code"),
      },
      setState: setIsSubmitting,
    });

    if (!err) {
      toast.success("Password reset successful", {
        description: "You can now login with your new password",
      });

      localStorage.removeItem("userEmail");
      localStorage.removeItem("code");

      navigate("/login");
    } else {
      toast.error("Password reset error", {
        description: err,
      });
    }
  };
  return (
    <motion.form
      key="reset"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      onSubmit={handleSubmit(onSubmit)}
      className="mt-5 w-full space-y-3"
    >
      <div className="space-y-1">
        <label
          htmlFor="currentPassword"
          className="font-archivo text-sm font-semibold"
        >
          New Password
        </label>

        <div className="inputClass flex w-full items-center">
          <input
            type={currentInputType}
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters long",
              },
            })}
            className="flex-1 border-none bg-transparent pr-0 outline-none"
          />

          <span
            onClick={toggleCurrentPasswordVisibility}
            className="cursor-pointer"
          >
            {currentInputType === "password" ? (
              <Eye size={20} />
            ) : (
              <EyeOff size={20} />
            )}
          </span>
        </div>
        {errors.currentPassword && (
          <p className="text-sm leading-[18px] font-medium text-red-400">
            {errors.currentPassword.message}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <label htmlFor="confirmPassword" className="text-sm font-medium">
          Type it again
        </label>

        <div className="inputClass">
          <input
            type={currentInputType}
            {...register("confirmPassword", {
              required: "Please confirm your password",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters long",
              },
              validate: (value) =>
                value === watch("password") || "Passwords do not match",
            })}
            className="w-full border-none bg-transparent pr-0 outline-none"
          />
        </div>
      </div>

      {errors.confirmPassword && (
        <p className="text-sm leading-[18px] font-medium text-red-400">
          {errors.confirmPassword.message}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn w-full rounded-md py-3 font-medium text-white disabled:pointer-events-none disabled:opacity-60"
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </button>
    </motion.form>
  );
};

export default NewPasswordForm;
